// src/components/VisitorContactWidget/VisitorContactWidget.jsx

import React from "react";

import { useLocation } from "react-router-dom";

import {
  FiMessageCircle,
  FiX,
} from "react-icons/fi";

import useVisitorContactWidget from "./hooks/useVisitorContactWidget";

import ContactWidgetLauncher from "./components/ContactWidgetLauncher";
import ContactWidgetHeader from "./components/ContactWidgetHeader";
import ContactWidgetHome from "./components/ContactWidgetHome";
import VisitorChatPanel from "./components/VisitorChatPanel";
import CallbackRequestForm from "./components/CallbackRequestForm";
import DemoRequestForm from "./components/DemoRequestForm";
import ContactSuccessPanel from "./components/ContactSuccessPanel";

import {
  CONTACT_WIDGET_VIEWS,
  IGNORED_WIDGET_PREFIXES,
} from "./data/contactWidgetData";

function getHeaderData(view) {
  switch (view) {
    case CONTACT_WIDGET_VIEWS.QUESTION:
      return {
        title: "Poser une question",
        subtitle:
          "Expliquez-moi votre besoin et échangeons directement.",
      };

    case CONTACT_WIDGET_VIEWS.CHAT:
      return {
        title: "Votre conversation",
        subtitle:
          "Les nouvelles réponses apparaissent automatiquement.",
      };

    case CONTACT_WIDGET_VIEWS.CALLBACK:
      return {
        title: "Être rappelé",
        subtitle:
          "Choisissez le meilleur moment pour être contacté.",
      };

    case CONTACT_WIDGET_VIEWS.DEMO:
      return {
        title: "Demander une démonstration",
        subtitle:
          "Une présentation personnalisée pour votre club.",
      };

    case CONTACT_WIDGET_VIEWS.SUCCESS:
      return {
        title: "Demande envoyée",
        subtitle:
          "Votre demande a bien été prise en compte.",
      };

    default:
      return {
        title: "MonAppliClub",
        subtitle:
          "Comment puis-je vous aider aujourd’hui ?",
      };
  }
}

export default function VisitorContactWidget() {
  const location = useLocation();

  const widget = useVisitorContactWidget();

  const ignored = IGNORED_WIDGET_PREFIXES.some(
    (prefix) =>
      location.pathname.startsWith(prefix),
  );

  if (ignored) {
    return null;
  }

  const header = getHeaderData(widget.view);

  const showBack =
    widget.view !== CONTACT_WIDGET_VIEWS.HOME;

  const handleBack = () => {
    widget.goHome();
  };

  return (
    <div className="fixed bottom-5 right-4 z-[9999] sm:bottom-6 sm:right-6">
      {widget.invitationVisible &&
        !widget.isOpen && (
          <div className="absolute bottom-[78px] right-0 w-[310px] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.22)]">
            <button
              type="button"
              onClick={widget.dismissInvitation}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-500 transition hover:bg-slate-200"
              aria-label="Fermer l'invitation"
            >
              <FiX />
            </button>

            <div className="bg-gradient-to-r from-[#123B57] to-[#2EA7E0] p-4 text-white">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 text-xl">
                <FiMessageCircle />
              </div>

              <h3 className="mt-3 font-semibold">
                Besoin d’une information ?
              </h3>

              <p className="mt-1 pr-6 text-xs leading-5 text-white/75">
                Je peux répondre à vos questions ou vous
                proposer une démonstration personnalisée.
              </p>
            </div>

            <div className="grid gap-2 p-3">
              <button
                type="button"
                onClick={() =>
                  widget.openWidget(
                    widget.conversationId
                      ? CONTACT_WIDGET_VIEWS.CHAT
                      : CONTACT_WIDGET_VIEWS.QUESTION,
                  )
                }
                className="rounded-xl bg-[#123B57] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#1C567F]"
              >
                Poser une question
              </button>

              <button
                type="button"
                onClick={() =>
                  widget.openWidget(
                    CONTACT_WIDGET_VIEWS.CALLBACK,
                  )
                }
                className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Être rappelé
              </button>
            </div>
          </div>
        )}

      {widget.isOpen && (
        <section className="absolute bottom-[78px] right-0 flex max-h-[calc(100vh-120px)] w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_28px_90px_rgba(15,23,42,0.28)] sm:w-[410px]">
          <ContactWidgetHeader
            title={header.title}
            subtitle={header.subtitle}
            showBack={showBack}
            onBack={handleBack}
            onClose={widget.closeWidget}
          />

          <div className="min-h-0 flex-1 overflow-y-auto">
            {widget.view ===
              CONTACT_WIDGET_VIEWS.HOME && (
              <ContactWidgetHome
                hasConversation={
                  Boolean(widget.conversationId)
                }
                onSelectView={widget.setView}
              />
            )}

            {[
              CONTACT_WIDGET_VIEWS.QUESTION,
              CONTACT_WIDGET_VIEWS.CHAT,
            ].includes(widget.view) && (
              <VisitorChatPanel
                conversationId={
                  widget.conversationId
                }
                messages={widget.messages}
                loadingConversation={
                  widget.loadingConversation
                }
                submitting={widget.submitting}
                errorMessage={
                  widget.errorMessage
                }
                storedContact={
                  widget.storedContact
                }
                onStartConversation={
                  widget.startQuestionConversation
                }
                onSendMessage={
                  widget.sendMessage
                }
              />
            )}

            {widget.view ===
              CONTACT_WIDGET_VIEWS.CALLBACK && (
              <CallbackRequestForm
                submitting={widget.submitting}
                errorMessage={
                  widget.errorMessage
                }
                storedContact={
                  widget.storedContact
                }
                suggestedProduct={
                  widget.suggestedProduct
                }
                onSubmit={
                  widget.submitCallbackRequest
                }
              />
            )}

            {widget.view ===
              CONTACT_WIDGET_VIEWS.DEMO && (
              <DemoRequestForm
                submitting={widget.submitting}
                errorMessage={
                  widget.errorMessage
                }
                storedContact={
                  widget.storedContact
                }
                suggestedProduct={
                  widget.suggestedProduct
                }
                onSubmit={
                  widget.submitDemoRequest
                }
              />
            )}

            {widget.view ===
              CONTACT_WIDGET_VIEWS.SUCCESS && (
              <ContactSuccessPanel
                title={
                  widget.successData?.title ||
                  "Demande envoyée"
                }
                message={
                  widget.successData?.message ||
                  "Votre demande a bien été enregistrée."
                }
                hasConversation={Boolean(
                  widget.conversationId,
                )}
                onHome={widget.goHome}
                onOpenConversation={() =>
                  widget.setView(
                    CONTACT_WIDGET_VIEWS.CHAT,
                  )
                }
              />
            )}
          </div>
        </section>
      )}

      <ContactWidgetLauncher
        isOpen={widget.isOpen}
        unreadCount={widget.unreadCount}
        onClick={() => {
          if (widget.isOpen) {
            widget.closeWidget();
          } else {
            widget.openWidget(
              CONTACT_WIDGET_VIEWS.HOME,
            );
          }
        }}
      />
    </div>
  );
}